import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { walletAPI, depositAPI, withdrawalAPI } from '../../utils/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Copy,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  ChevronDown,
  Info,
} from 'lucide-react';

const DepositWithdraw = () => {
  // State Management
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('deposit');

  // Deposit States
  const [depositAsset, setDepositAsset] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositAddress, setDepositAddress] = useState('');
  const [depositLoading, setDepositLoading] = useState(false);
  const [showDepositDialog, setShowDepositDialog] = useState(false);

  // Withdrawal States
  const [withdrawAsset, setWithdrawAsset] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawDestination, setWithdrawDestination] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingWithdrawalId, setPendingWithdrawalId] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);

  // History
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchWallets(), fetchDeposits(), fetchWithdrawals()]);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWallets = async () => {
    try {
      const response = await walletAPI.getAll();
      setWallets(response.data || []);
      // Auto-select first asset
      if (response.data.length > 0 && !depositAsset) {
        setDepositAsset(response.data[0].asset_symbol);
        setWithdrawAsset(response.data[0].asset_symbol);
      }
    } catch (error) {
      console.error('Failed to load wallets:', error);
    }
  };

  const fetchDeposits = async () => {
    try {
      const response = await depositAPI.getAll();
      setDeposits(response.data || []);
    } catch (error) {
      console.error('Failed to load deposits:', error);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const response = await withdrawalAPI.getAll();
      setWithdrawals(response.data || []);
    } catch (error) {
      console.error('Failed to load withdrawals:', error);
    }
  };

  // Deposit Handler
  const handleGenerateDepositAddress = async (e) => {
    e.preventDefault();
    if (!depositAsset) {
      toast.error('Please select an asset');
      return;
    }

    setDepositLoading(true);
    try {
      const response = await depositAPI.request({
        asset_symbol: depositAsset,
        amount: depositAmount ? parseFloat(depositAmount) : 0,
        type: 'crypto',
      });

      if (response.data.wallet_address) {
        setDepositAddress(response.data.wallet_address);
        toast.success('Deposit address generated successfully!');
      } else {
        toast.error('Failed to generate deposit address');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate deposit address');
      console.error(error);
    } finally {
      setDepositLoading(false);
    }
  };

  const copyToClipboard = (text, label = 'Address') => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  // Withdrawal Handler
  const handleRequestWithdrawal = async (e) => {
    e.preventDefault();
    if (!withdrawAsset || !withdrawAmount || !withdrawDestination) {
      toast.error('Please fill all fields');
      return;
    }

    const selectedWallet = wallets.find(w => w.asset_symbol === withdrawAsset);
    if (!selectedWallet || selectedWallet.available_balance < parseFloat(withdrawAmount)) {
      toast.error('Insufficient balance');
      return;
    }

    setWithdrawLoading(true);
    try {
      const response = await withdrawalAPI.request({
        asset_symbol: withdrawAsset,
        amount: parseFloat(withdrawAmount),
        type: 'crypto',
        destination: withdrawDestination,
      });

      setPendingWithdrawalId(response.data.withdrawal_id);
      setShowWithdrawDialog(false);
      setShowVerificationDialog(true);
      toast.success('Verification code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request withdrawal');
      console.error(error);
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Verification Handler
  const handleVerifyWithdrawal = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length < 4) {
      toast.error('Please enter a valid verification code');
      return;
    }

    setVerificationLoading(true);
    try {
      await withdrawalAPI.verify(pendingWithdrawalId, verificationCode);
      toast.success('Withdrawal confirmed and submitted successfully!');
      setShowVerificationDialog(false);
      setVerificationCode('');
      setWithdrawAmount('');
      setWithdrawDestination('');
      setPendingWithdrawalId(null);
      await fetchWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
      console.error(error);
    } finally {
      setVerificationLoading(false);
    }
  };

  const getSelectedWallet = (asset) => wallets.find(w => w.asset_symbol === asset);
  const selectedDepositWallet = getSelectedWallet(depositAsset);
  const selectedWithdrawWallet = getSelectedWallet(withdrawAsset);

  const cryptoWallets = wallets.filter(w => w.asset_symbol !== 'USD');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #062136;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
        `}</style>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8 animate-fadeIn p-10">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-black mb-2">Deposit & Withdraw</h1>
        <p className="text-gray-600">Manage your cryptocurrency deposits and withdrawals</p>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid h-auto w-full max-w-md grid-cols-2 rounded-xl border border-gray-300 bg-gray-100 p-1 overflow-hidden">
          <TabsTrigger
            value="deposit"
            className="h-8 rounded-lg text-gray-800 hover:bg-gray-200 hover:text-gray-900 data-[state=active]:bg-[#062136] dark:data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-none"
          >
            <ArrowDownCircle className="w-4 h-4 mr-2" />
            Deposit
          </TabsTrigger>
          <TabsTrigger
            value="withdraw"
            className="h-8 rounded-lg text-gray-800 hover:bg-gray-200 hover:text-gray-900 data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:shadow-none"
          >
            <ArrowUpCircle className="w-4 h-4 mr-2" />
            Withdraw
          </TabsTrigger>
        </TabsList>

        {/* DEPOSIT TAB */}
        <TabsContent value="deposit" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Deposit Form */}
            <div className="lg:col-span-2">
              <Card className="p-6 border border-gray-200 shadow-sm">
                <h2 className="text-2xl font-bold text-black mb-6 flex items-center">
                  <ArrowDownCircle className="w-6 h-6 mr-3 text-[#0F4C5C]" />
                  Deposit Cryptocurrency
                </h2>

                <form onSubmit={handleGenerateDepositAddress} className="space-y-6">
                  {/* Asset Selection */}
                  <div>
                    <Label className="text-black font-semibold">Select Cryptocurrency</Label>
                    <Select value={depositAsset} onValueChange={setDepositAsset}>
                      <SelectTrigger className="mt-2 h-12 border border-gray-300">
                        <SelectValue placeholder="Choose cryptocurrency" />
                      </SelectTrigger>
                      <SelectContent>
                        {cryptoWallets.map((wallet) => (
                          <SelectItem key={wallet.asset_symbol} value={wallet.asset_symbol}>
                            <div className="flex items-center">
                              <span className="font-semibold">{wallet.asset_symbol}</span>
                              <span className="ml-2 text-gray-500 text-sm">
                                Balance: {wallet.balance.toFixed(6)}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Amount Input */}
                  <div>
                    <Label className="text-black font-semibold">Amount (Optional)</Label>
                    <div className="flex items-center mt-2">
                      <Input
                        type="number"
                        step="0.00000001"
                        min="0"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        className="h-12 border border-gray-300"
                        placeholder="0.00"
                      />
                      {depositAsset && (
                        <span className="ml-2 px-3 py-2 bg-gray-100 text-gray-700 font-semibold rounded">
                          {depositAsset}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Leave empty to generate a deposit address for any amount
                    </p>
                  </div>

                  {/* Generate Address Button */}
                  <Button
                    type="submit"
                    disabled={depositLoading || !depositAsset}
                    className="w-full h-12 bg-[#062136] hover:bg-[#062136]/90 text-white font-bold text-lg"
                  >
                    {depositLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <ArrowDownCircle className="w-4 h-4 mr-2" />
                        Generate Deposit Address
                      </>
                    )}
                  </Button>
                </form>

                {/* Important Notes */}
                <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-700">
                      <p className="font-semibold mb-2">Important Information</p>
                      <ul className="space-y-1">
                        <li>• Only send the selected cryptocurrency to the provided address</li>
                        <li>• Minimum 3 network confirmations required</li>
                        <li>• Once confirmed, funds will be available in your wallet</li>
                        <li>• Double-check the address before sending funds</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Deposit Address Display */}
            <div>
              <Card className="p-6 border border-gray-200 shadow-sm sticky top-20">
                <h3 className="font-bold text-black mb-4">Deposit Address</h3>

                {depositAddress ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center text-green-700 mb-2">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        <span className="font-semibold text-sm">Address Generated</span>
                      </div>
                      <div className="break-all font-mono text-xs text-gray-700 mb-3 p-2 bg-white rounded border border-green-100">
                        {depositAddress}
                      </div>
                      <Button
                        onClick={() => copyToClipboard(depositAddress, 'Address')}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        size="sm"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Address
                      </Button>
                    </div>

                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs text-amber-700">
                        <span className="font-semibold">Minimum amount:</span> Varies by network
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        <span className="font-semibold">Confirmations:</span> 3+
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <ArrowDownCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      Generate an address to get started
                    </p>
                  </div>
                )}
              </Card>
            </div>
          </div>

          {/* Recent Deposits */}
          <Card className="p-6 border border-gray-200 shadow-sm overflow-hidden">
            <h3 className="text-lg font-bold text-black mb-4">Recent Deposits</h3>
            {deposits.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No deposits yet</p>
            ) : (
              <div className="responsive-table-wrapper">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 font-semibold text-gray-700">Asset</th>
                      <th className="text-right py-3 font-semibold text-gray-700">Amount</th>
                      <th className="text-left py-3 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 font-semibold text-gray-700">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deposits.slice(0, 5).map((deposit) => (
                      <tr key={deposit.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 font-semibold text-black">{deposit.asset_symbol}</td>
                        <td className="text-right py-3 text-black">{deposit.amount.toFixed(6)}</td>
                        <td className="py-3">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${deposit.status === 'confirmed'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                              }`}
                          >
                            {deposit.status}
                          </span>
                        </td>
                        <td className="py-3 text-gray-600">
                          {new Date(deposit.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* WITHDRAW TAB */}
        <TabsContent value="withdraw" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Withdrawal Form */}
            <div className="lg:col-span-2">
              <Card className="p-6 border border-gray-200 shadow-sm">
                <h2 className="text-2xl font-bold text-black mb-6 flex items-center">
                  <ArrowUpCircle className="w-6 h-6 mr-3 text-red-600" />
                  Withdraw Cryptocurrency
                </h2>

                <form onSubmit={handleRequestWithdrawal} className="space-y-6">
                  {/* Asset Selection */}
                  <div>
                    <Label className="text-black font-semibold">Select Cryptocurrency</Label>
                    <Select value={withdrawAsset} onValueChange={setWithdrawAsset}>
                      <SelectTrigger className="mt-2 h-12 border border-gray-300">
                        <SelectValue placeholder="Choose cryptocurrency" />
                      </SelectTrigger>
                      <SelectContent>
                        {cryptoWallets.map((wallet) => (
                          <SelectItem key={wallet.asset_symbol} value={wallet.asset_symbol}>
                            <div className="flex items-center">
                              <span className="font-semibold">{wallet.asset_symbol}</span>
                              <span className="ml-2 text-gray-500 text-sm">
                                Available: {wallet.available_balance.toFixed(6)}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Destination Address */}
                  <div>
                    <Label className="text-black font-semibold">Destination Address</Label>
                    <Input
                      type="text"
                      value={withdrawDestination}
                      onChange={(e) => setWithdrawDestination(e.target.value)}
                      className="mt-2 h-12 border border-gray-300"
                      placeholder="0x... (your withdrawal address)"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Double-check this address - withdrawals cannot be reversed
                    </p>
                  </div>

                  {/* Amount Input */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-black font-semibold">Amount</Label>
                      {selectedWithdrawWallet && (
                        <button
                          type="button"
                          onClick={() =>
                            setWithdrawAmount(selectedWithdrawWallet.available_balance.toString())
                          }
                          className="text-xs font-semibold text-[#0F4C5C] hover:underline"
                        >
                          Max: {selectedWithdrawWallet.available_balance.toFixed(6)}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center mt-2">
                      <Input
                        type="number"
                        step="0.00000001"
                        min="0"
                        max={selectedWithdrawWallet?.available_balance || 0}
                        value={withdrawAmount}
                        onChange={(e) => setWithdrawAmount(e.target.value)}
                        className="h-12 border border-gray-300"
                        placeholder="0.00"
                        required
                      />
                      {withdrawAsset && (
                        <span className="ml-2 px-3 py-2 bg-gray-100 text-gray-700 font-semibold rounded">
                          {withdrawAsset}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Withdraw Button */}
                  <Button
                    type="submit"
                    disabled={withdrawLoading || !withdrawAsset || !withdrawAmount || !withdrawDestination}
                    className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-bold text-lg"
                  >
                    {withdrawLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ArrowUpCircle className="w-4 h-4 mr-2" />
                        Request Withdrawal
                      </>
                    )}
                  </Button>
                </form>

                {/* Important Notes */}
                <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-red-700">
                      <p className="font-semibold mb-2">Withdrawal Important Notes</p>
                      <ul className="space-y-1">
                        <li>• Withdrawals require email verification</li>
                        <li>• Check address carefully - funds cannot be recovered if wrong</li>
                        <li>• Processing time: 5-30 minutes depending on network</li>
                        <li>• Network fees will be deducted from the amount</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Withdrawal Summary */}
            <div>
              <Card className="p-6 border border-gray-200 shadow-sm sticky top-20">
                <h3 className="font-bold text-black mb-4">Withdrawal Summary</h3>

                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Amount</p>
                    <p className="text-2xl font-bold text-black">
                      {withdrawAmount || '0'} {withdrawAsset || 'USD'}
                    </p>
                  </div>

                  {selectedWithdrawWallet && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-600 mb-1">Available Balance</p>
                      <p className="text-lg font-bold text-blue-700">
                        {selectedWithdrawWallet.available_balance.toFixed(6)} {withdrawAsset}
                      </p>
                    </div>
                  )}

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                    <div>
                      <p className="text-xs text-amber-600 font-semibold">Network</p>
                      <p className="text-sm text-amber-700">Polygon {withdrawAsset}</p>
                    </div>
                    <div>
                      <p className="text-xs text-amber-600 font-semibold">Processing</p>
                      <p className="text-sm text-amber-700">5-30 minutes</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Verification Dialog */}
          <Dialog open={showVerificationDialog} onOpenChange={setShowVerificationDialog}>
            <DialogContent className="border border-gray-200 shadow-lg">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-black">Verify Withdrawal</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    A verification code has been sent to your email. Enter it below to confirm your withdrawal.
                  </p>
                </div>

                <form onSubmit={handleVerifyWithdrawal} className="space-y-4">
                  <div>
                    <Label className="text-black font-semibold">Verification Code</Label>
                    <Input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="mt-2 h-12 text-center text-2xl tracking-widest border border-gray-300"
                      placeholder="000000"
                      maxLength="6"
                      pattern="[0-9]{6}"
                      autoFocus
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={verificationLoading || verificationCode.length < 4}
                    className="w-full h-12 bg-[#062136] hover:bg-[#062136]/90 text-white font-bold"
                  >
                    {verificationLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Confirm Withdrawal
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </DialogContent>
          </Dialog>

          {/* Recent Withdrawals */}
          <Card className="p-6 border border-gray-200 shadow-sm overflow-hidden">
            <h3 className="text-lg font-bold text-black mb-4">Recent Withdrawals</h3>
            {withdrawals.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No withdrawals yet</p>
            ) : (
              <div className="responsive-table-wrapper">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 font-semibold text-gray-700">Asset</th>
                      <th className="text-right py-3 font-semibold text-gray-700">Amount</th>
                      <th className="text-left py-3 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 font-semibold text-gray-700">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.slice(0, 5).map((withdrawal) => (
                      <tr key={withdrawal.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 font-semibold text-black">{withdrawal.asset_symbol}</td>
                        <td className="text-right py-3 text-black">{withdrawal.amount.toFixed(6)}</td>
                        <td className="py-3">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center w-fit ${withdrawal.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : withdrawal.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                              }`}
                          >
                            {withdrawal.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                            {withdrawal.status}
                          </span>
                        </td>
                        <td className="py-3 text-gray-600">
                          {new Date(withdrawal.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DepositWithdraw;
